import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CLOSE_DELAY_MS = 220;

const MainMenu = ({ style = "" }: { style?: string }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Shared open/close handlers with a short grace period on close, so
  // crossing the visual gap between a nav button and its dropdown panel
  // (or moving briefly outside while heading for a menu item) doesn't
  // slam the menu shut before the click registers.
  const openMenu = (key: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenSubmenu(key);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setOpenSubmenu(null);
      closeTimer.current = null;
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const visaItems = [
    { name: t("visaApplication"), routePath: "/search-visa", desc: t("visaApplicationDesc") },
    { name: t("trackApplication"), routePath: "/track-application", desc: t("trackApplicationDesc") }
  ];

  const meSchengenItems = [
    { name: t("schengenVisa"), routePath: "/schengen-visa", desc: t("schengenVisaDesc") },
    { name: t("dummyTicket"), routePath: "/dummy-ticket", desc: t("dummyTicketDesc") },
    { name: t("travelInsurance"), routePath: "/insurance", desc: t("travelInsuranceDesc") }
  ];

  const countryVisaItems = [
    { name: t("oman"), routePath: "/visa-oman", desc: t("visaForOman") },
    { name: t("uae"), routePath: "/visa-uae", desc: t("visaForUae") },
    { name: t("saudiArabia"), routePath: "/visa-ksa", desc: t("visaForKsa") },
    { name: t("bahrain"), routePath: "/visa-bahrain", desc: t("visaForBahrain") },
    { name: t("kuwait"), routePath: "/visa-kuwait", desc: t("visaForKuwait") },
    { name: t("qatar"), routePath: "/visa-qatar", desc: t("visaForQatar") },
    { name: t("thailand"), routePath: "/visa-thailand", desc: t("visaForThailand") },
    { name: t("india"), routePath: "/visa-india", desc: t("visaForIndia") },
    { name: t("armenia"), routePath: "/visa-armenia", desc: t("visaForArmenia") },
    { name: t("usa"), routePath: "/visa-usa", desc: t("visaForUsa") },
    { name: t("canada"), routePath: "/visa-canada", desc: t("visaForCanada") },
  ];

  const isActive = (path: string) => pathname === path;

  return (
    <nav className="main-nav-wrapper">
      <ul className="nav-list">
        <li>
          <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
            {t("home")}
          </Link>
        </li>

        {/* Visas Dropdown */}
        <li
          className="nav-item-dropdown"
          onMouseEnter={() => openMenu('visas')}
          onMouseLeave={scheduleClose}
        >
          <button className={`nav-link ${visaItems.some(i => i.routePath === pathname) ? 'active' : ''}`}>
            {t("visas")}
            <svg className="chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div
            className={`dropdown-menu ${openSubmenu === 'visas' ? 'show' : ''}`}
            onMouseEnter={() => openMenu('visas')}
            onMouseLeave={scheduleClose}
          >
            {visaItems.map((item, idx) => (
              <Link key={idx} to={item.routePath} className="dropdown-item" onClick={() => setOpenSubmenu(null)}>
                <div className="item-title">{item.name}</div>
                <div className="item-desc">{item.desc}</div>
              </Link>
            ))}
          </div>
        </li>

        {/* Schengen Dropdown */}
        <li
          className="nav-item-dropdown"
          onMouseEnter={() => openMenu('schengen')}
          onMouseLeave={scheduleClose}
        >
          <button className={`nav-link ${meSchengenItems.some(i => i.routePath === pathname) ? 'active' : ''}`}>
            {t("schengen")}
            <svg className="chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div
            className={`dropdown-menu ${openSubmenu === 'schengen' ? 'show' : ''}`}
            onMouseEnter={() => openMenu('schengen')}
            onMouseLeave={scheduleClose}
          >
            {meSchengenItems.map((item, idx) => (
              <Link key={idx} to={item.routePath} className="dropdown-item" onClick={() => setOpenSubmenu(null)}>
                <div className="item-title">{item.name}</div>
                <div className="item-desc">{item.desc}</div>
              </Link>
            ))}
          </div>
        </li>

        {/* Other Countries Dropdown */}
        <li
          className="nav-item-dropdown"
          onMouseEnter={() => openMenu('countries')}
          onMouseLeave={scheduleClose}
        >
          <button className={`nav-link ${countryVisaItems.some(i => i.routePath === pathname) ? 'active' : ''}`}>
            {t("otherCountries")}
            <svg className="chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div
            className={`dropdown-menu grid-2col ${openSubmenu === 'countries' ? 'show' : ''}`}
            onMouseEnter={() => openMenu('countries')}
            onMouseLeave={scheduleClose}
          >
            {countryVisaItems.map((item, idx) => (
              <Link key={idx} to={item.routePath} className="dropdown-item" onClick={() => setOpenSubmenu(null)}>
                <div className="item-title">{item.name}</div>
                <div className="item-desc">{item.desc}</div>
              </Link>
            ))}
          </div>
        </li>

        <li>
          <Link to="/trips" className={`nav-link ${isActive('/trips') ? 'active' : ''}`}>
            {t("tours")}
          </Link>
        </li>

        <li>
          <Link to="/services" className={`nav-link ${isActive('/services') ? 'active' : ''}`}>
            Services
            <span className="badge-promo">{t("hot")}</span>
          </Link>
        </li>

        <li>
          <Link to="/flights" className={`nav-link ${isActive('/flights') ? 'active' : ''}`}>
            {t("flights")}
          </Link>
        </li>

        <li>
          <Link to="/car-rent" className={`nav-link ${isActive('/car-rent') ? 'active' : ''}`}>
            {t("carRental")}
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default MainMenu;